import axiosInstance from '../axiosInstance'

// Models
import { Site, SiteID } from '../sites/models/site'
import {
    Group,
    GroupJson,
    GroupCreateForm,
    GroupUpdateForm,
    GroupID,
} from './models/group'
import { Pagination } from '../common/models/pagination'
import { User } from '../users/models/user'
import { EmployeeID } from '../employees/models/employee'

interface GroupCreateData {
    name: string
    short_name: string
    description: string
}

interface GroupUpdateData {
    name?: string
    short_name?: string
    description?: string
}

export interface GroupSearchForm {
    name?: string
    eligibleForUser?: User

    showArchived?: boolean

    perPage: number
    page: number
}

interface GroupSearchParams {
    name?: string
    eligible_for_user_id?: number

    show_archived?: 1 | 0

    per_page: number
    page: number
}

export const fetch = async (siteId: SiteID, form: GroupSearchForm): Promise<{ groups: Group[], pagination: Pagination }> => {
    const params: GroupSearchParams = {
        per_page: form.perPage,
        page: form.page,
    }

    const name = form.name?.trim() ?? ''
    if (name.length > 0) {
        params.name = name
    }

    if (form.eligibleForUser) {
        params.eligible_for_user_id = form.eligibleForUser.id
    }

    if (form.showArchived) {
        params.show_archived = 1
    }

    const response = await axiosInstance.get(`api/orgs/${siteId}/groups`, { params })

    const data = response.data.data as GroupJson[]

    return {
        groups: data.map((group) => {
            return Group.fromJson(group)
        }),
        pagination: new Pagination(response.data.meta.pagination)
    }
}

export const fetchByEmployee = async (siteId: SiteID, employeeId: EmployeeID, form: GroupSearchForm): Promise<{ groups: Group[], pagination: Pagination }> => {
    const params: GroupSearchParams = {
        per_page: form.perPage,
        page: form.page,
    }

    const name = form.name?.trim() ?? ''
    if (name.length > 0) {
        params.name = name
    }

    const response = await axiosInstance.get(`api/orgs/${siteId}/employees/${employeeId}/groups`, { params })

    const data = response.data.data as GroupJson[]

    return {
        groups: data.map((group) => {
            return Group.fromJson(group)
        }),
        pagination: new Pagination(response.data.meta.pagination)
    }
}

export const create = async (site: Site, form: GroupCreateForm): Promise<{ group: Group }> => {
    const data: GroupCreateData = {
        name: form.name,
        short_name: form.shortName,
        description: form.description,
    }

    const response = await axiosInstance.post(`api/orgs/${site.id}/groups`, data)

    const groupJson = response.data.data as GroupJson

    return {
        group: Group.fromJson(groupJson)
    }
}

export const show = async (siteId: SiteID, groupId: GroupID): Promise<{ group: Group }> => {
    const response = await axiosInstance.get(`api/orgs/${siteId}/groups/${groupId}`)

    const data = response.data.data as GroupJson

    return {
        group: Group.fromJson(data),
    }
}

function updateFormToData(form: GroupUpdateForm): GroupUpdateData {
    const data: GroupUpdateData = {}

    if ((form.name?.trim().length ?? 0) > 0) {
        data.name = form.name
    }

    return data
}

export const update = async (siteId: SiteID, groupId: GroupID, form: GroupUpdateForm): Promise<{ group: Group }> => {
    const data = updateFormToData(form)

    const response = await axiosInstance.patch(`api/orgs/${siteId}/groups/${groupId}`, data)

    const groupJson = response.data.data as GroupJson

    return {
        group: Group.fromJson(groupJson)
    }
}

export const archive = async (siteId: SiteID, groupId: GroupID): Promise<void> => {
    await axiosInstance.put(`api/orgs/${siteId}/groups/${groupId}/archive`)
}

export const restore = async (siteId: SiteID, groupId: GroupID): Promise<void> => {
    await axiosInstance.delete(`api/orgs/${siteId}/groups/${groupId}/archive`)
}